import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ViewChild,
  signal,
  ChangeDetectorRef
} from '@angular/core';
import { ZefReactiveComponent } from '@zerops/zef/core';
import { authActions } from '@zerops/zef/auth';
import { loadUser, storeUserData, UserEntity } from '@zerops/zerops/core/user-base';
import { canCreateProjects, canViewFinances, ClientUser, ClientUserRole, hasAdminRoleOrHigher, hasBasicUserRoleOrHigher } from '@zerops/models/client-user';
import { AppVersionEntity } from '@zerops/zerops/core/app-version-base';
import { NotificationsPopService, notificationsPopSetSettings } from '@zerops/zerops/feature/notifications-pop';
import { ProcessesPopService, processesPopSetSettings } from '@zerops/zerops/feature/processes-pop';
import { AppVersionStatuses } from '@zerops/models/app-version';
import { delayWhen, distinctUntilChanged, pairwise, map, delay, filter, withLatestFrom } from 'rxjs/operators';
import { Subject, timer } from 'rxjs';
import { NotificationEntity } from '@zerops/zerops/core/notification-base';
import { SatPopover } from '@zerops/zef/popover';
import { ProcessEntity } from '@zerops/zerops/core/process-base';
import { ProcessStatuses } from '@zerops/models/process';
// import { UserStatuses } from '@zerops/models/user';
import { AppBarTranslations } from './app-bar.translations';
import { FEATURE_NAME } from './app-bar.constant';
import { ColorScheme, ThemeService } from '@zerops/zef/theme';
import { ImportExportDialogModes, importExportDialogOpen } from '../import-export-dialog';
import { Noola } from '@zerops/zef/noola';
import { environment } from '@zerops/zerops/env';
import { zefDialogOpen } from '@zerops/zef/dialog';
import { CLIENT_ADD_DIALOG_FEATURE_NAME } from '@zerops/zerops/feature/client-add-dialog';
import { resendVerificationEmail } from '@zerops/zerops/feature/email-verification-notification';
import { COMMAND_PALETTE_FEATURE_NAME } from '@zerops/zerops/feature/command-palette';

@Component({
  selector: 'z-app-bar',
  templateUrl: './app-bar.container.html',
  styleUrls: [ './app-bar.container.scss' ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppBarContainer extends ZefReactiveComponent {

  // # Event Streams
  onLogout$ = new Subject<void>();
  onOpenNotifications$ = new Subject<void>();
  onOpenProcesses$ = new Subject<void>();
  onSelectClientUser$ = new Subject<ClientUser>();
  onImportProject$ = new Subject<void>();
  onCreateOrganization$ = new Subject<void>();
  onReloadUserInfo$ = new Subject<void>();
  onResendVerification$ = new Subject<void>();
  onOpenCommandPalette$ = new Subject<void>();

  // # Data
  // -- sync
  notificationPopRef: SatPopover;
  processesPopRef: SatPopover;
  resendKey = resendVerificationEmail.type;
  enableNoola = environment.enableNoola === 'true';
  isMac = /mac/i.test(navigator.platform);
  shortcutLabel = this.isMac ? '⌘K' : 'ctrl K';
  searchTooltip = 'Search & commands';
  // userStatuses = UserStatuses;

  // -- async
  noolaState = signal(false);
  appBarTranslations$ = this.translate$<AppBarTranslations>(FEATURE_NAME);
  clientUser$ = this._userEntity
    .activeClientUser$
    .pipe(
      distinctUntilChanged(),
      pairwise(),
      delayWhen(([ pre, post ]) => pre && !post ? timer(400) : timer(0)),
      map(([ _, post ]) => post),
    );
  isContabo$ = this._userEntity.isContabo$;
  activeUser$ = this._userEntity.activeUser$;
  emailNotVerified$ = this.activeUser$.pipe(
    map((user) => !!user && user.emailVerified === false),
    distinctUntilChanged()
  );
  activeUserClientUsers$ = this._userEntity.activeUserClientUsers$.pipe(
    distinctUntilChanged(),
    pairwise(),
    delayWhen(([ pre, post ]) => pre && !post ? timer(400) : timer(0)),
    map(([ _, post ]) => post),
    filter((d) => !!d)
  );
  appVersionUploading$ = this._appVersionEntity
    .list$()
    .pipe(
      map((d) => d?.filter((appVersion) => appVersion.status === AppVersionStatuses.Uploading).length)
    );
  unreadNotificationsCount$ = this._notificationEntity
    .unreadNotificationMap$()
    .pipe(
      map((d) => d.client),
      distinctUntilChanged()
    );
  processesCount$ = this._processEntity
    .list$(undefined, [ 'created' ], [ 'asc' ])
    .pipe(
      map((items) => !items ? [] : items),
      map((items) => items.reduce((res, itm) => {
        if (itm.status === ProcessStatuses.RUNNING) {
          res.running = res.running + 1;
        }

        if (itm.status === ProcessStatuses.PENDING) {
          res.queued = res.queued + 1;
        }

        return res;
      }, {
        running: 0,
        queued: 0
      }))
    );
  // Role Permissions
  activeClientUser$ = this._userEntity.activeClientUser$.pipe(
    filter((d) => !!d)
  );
  hasAdminRoleOrHigher$ = this.activeClientUser$.pipe(
    map((c) => hasAdminRoleOrHigher(c.roleCode)),
    distinctUntilChanged()
  );
  canCreateProjects$ = this.activeClientUser$.pipe(
    map((d) => canCreateProjects(d)),
    distinctUntilChanged()
  );
  hasOwnerRole$ = this.activeClientUser$.pipe(
    map((c) => c.roleCode === ClientUserRole.Owner),
    distinctUntilChanged()
  );
  hasBasicUserRoleOrHigher$ = this.activeClientUser$.pipe(
    map((c) => hasBasicUserRoleOrHigher(c.roleCode)),
    distinctUntilChanged()
  );
  canViewFinances$ = this.activeClientUser$.pipe(
    map((c) => canViewFinances(c)),
    distinctUntilChanged()
  );

  // -- angular
  @Input()
  active: boolean;

  @Input()
  menuState: boolean;

  @Input()
  menuAnchor: any;

  @Input()
  logoTheme: 'white' | 'black' = 'black';

  @Output()
  menuClicked = new EventEmitter<void>();

  @ViewChild('menuPopRef')
  menuPopRef: SatPopover;

  // # State resolver
  state = this.$connect({
    appBarTranslations: this.appBarTranslations$,
    clientUser: this.clientUser$,
    activeUserClientUsers: this.activeUserClientUsers$,
    appVersionUploading: this.appVersionUploading$,
    unreadNotificationsCount: this.unreadNotificationsCount$,
    processesCount: this.processesCount$,
    isContabo: this.isContabo$,
    activeUser: this.activeUser$,
    isLightMode: this._theme.isDarkMode$.pipe(map((d) => !d)),
    activeTheme: this._theme.activeMode$,
    isDarkTheme: this._theme.isDarkMode$,
    isZen: this._theme.isZen$,
    isCompact: this._theme.isCompact$,
    hasAdminRoleOrHigher: this.hasAdminRoleOrHigher$,
    hasOwnerRole: this.hasOwnerRole$,
    canCreateProjects: this.canCreateProjects$,
    hasBasicUserRoleOrHigher: this.hasBasicUserRoleOrHigher$,
    canViewFinances: this.canViewFinances$,
    emailNotVerified: this.emailNotVerified$
  });

  // # Action Streams
  private _logoutAction$ = this.onLogout$.pipe(map(() => authActions.logout()));
  private _selectClientUserAction$ = this.onSelectClientUser$.pipe(
    map(({ userId, id }) => storeUserData(userId, id, true)),
    delay(400)
  );
  private _openNotificationsAction$ = this.onOpenNotifications$.pipe(
    map(() => notificationsPopSetSettings({ level: 'client' }))
  );
  private _openProcessesAction$ = this.onOpenProcesses$.pipe(
    map(() => processesPopSetSettings({ level: 'client' }))
  );
  private _importExportDialogOpenAction$ = this.onImportProject$.pipe(
    map(() => importExportDialogOpen({ mode: ImportExportDialogModes.Import }))
  );
  #onCreateOrganizationAction$ = this.onCreateOrganization$.pipe(
    map(() => zefDialogOpen({ key: CLIENT_ADD_DIALOG_FEATURE_NAME }))
  );
  #onReloadUserInfoAction$ = this.onReloadUserInfo$.pipe(
    map(() => loadUser())
  );
  #openCommandPaletteAction$ = this.onOpenCommandPalette$.pipe(
    map(() => zefDialogOpen({ key: COMMAND_PALETTE_FEATURE_NAME }))
  );
  #resendVerificationAction$ = this.onResendVerification$.pipe(
    withLatestFrom(this.activeUser$),
    filter(([ , user ]) => !!user?.id),
    map(([ , user ]) => resendVerificationEmail({ id: user.id }, { type: 'pop' }))
  );

  constructor(
    private _userEntity: UserEntity,
    private _notificationEntity: NotificationEntity,
    private _processEntity: ProcessEntity,
    private _appVersionEntity: AppVersionEntity,
    private _notificationPopService: NotificationsPopService,
    private _processesPopService: ProcessesPopService,
    private _theme: ThemeService,
    private _noola: Noola,
    private _cdRef: ChangeDetectorRef
  ) {
    super();

    this.processesPopRef = this._processesPopService.getRef();
    this.notificationPopRef = this._notificationPopService.getRef();

    this._noola.onOpen(() => {
      this.noolaState.set(true);
      this._cdRef.detectChanges();
    });
    this._noola.onClose(() => {
      this.noolaState.set(false);
      this._cdRef.detectChanges();
    });

    // # Dispatcher
    this.$dispatchActions([
      this._logoutAction$,
      this._selectClientUserAction$,
      this._openNotificationsAction$,
      this._openProcessesAction$,
      this._importExportDialogOpenAction$,
      this.#onCreateOrganizationAction$,
      this.#onReloadUserInfoAction$,
      this.#resendVerificationAction$,
      this.#openCommandPaletteAction$
    ]);

  }

  setTheme(theme: ColorScheme) {
    this._theme.saveTheme(theme);
  }

  toggleZen() {
    this._theme.toggleZenMode();
  }

  toggleCompact() {
    this._theme.toggleCompactMode();

    setTimeout(() => {
      this.menuPopRef.realign();
    });
  }

  showNoola() {
    this._noola.open();
  }

  hideNoola() {
    this._noola.close();
  }

}
